import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Star, Gift, QrCode, Coffee, UtensilsCrossed, Share2 } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];
function todayLabel() {
  const idx = new Date().getDay(); // 0 Sun
  return DAYS[(idx + 6) % 7] || "Montag";
}

export default function Home() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [todayItems, setTodayItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/branches").then((r) => {
      setBranches(r.data);
      if (r.data.length) setBranchId(r.data[0].id);
    });
    api.get("/offers").then((r) => setOffers(r.data.slice(0, 3)));
  }, []);

  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/account/overview").then((r) => {
        setOverview(r.data);
        if (r.data.user?.preferred_branch_id) setBranchId(r.data.user.preferred_branch_id);
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!branchId) return;
    api.get(`/menus?branch_id=${branchId}`).then((r) => {
      const today = new Date().toISOString().split("T")[0];
      const withWs = (r.data || []).filter((m) => m.week_start);
      const cur = [...withWs].reverse().find((m) => m.week_start <= today) || withWs[withWs.length - 1] || r.data[0];
      if (cur) setTodayItems((cur.days?.[todayLabel()] || []).slice(0, 3));
      else setTodayItems([]);
    });
  }, [branchId]);

  const hero = settings?.homepage || {};
  const nextReward = overview?.next_reward;
  const pointsToNext = nextReward ? nextReward.points_required - (overview?.user?.points || 0) : 0;

  return (
    <div>
      <Seo title="HELIUM – Herz des Krankenhauses | Cafeteria & Catering" description="Frisches Essen, Kaffee, Snacks und Catering in Krankenhäusern. Wochenmenü, Angebote und das HELIUM Vorteile Treueprogramm." />

      {/* Hero */}
      <section className="relative min-h-[68vh] lg:min-h-[82vh] flex flex-col">
        <div className="absolute inset-0">
          <img src={hero.hero_image} alt="HELIUM Cafeteria" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/20 to-black/30" />
        </div>

        {/* Text oben rechts */}
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 flex-1">
          <div className="mr-auto max-w-md text-left">
            <span className="cine cine-1 inline-flex items-center gap-2 bg-[#E0243A] text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
              Herz des Krankenhauses
            </span>
            <h1 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight font-display drop-shadow-lg">
              <span className="cine cine-2 block"><span className="text-[#E0243A]">HELIUM</span>. Mehr als eine Pause…</span>
              <span className="cine cine-3 block mt-1">Essen.</span>
              <span className="cine cine-3 block">Genießen.</span>
              <span className="cine cine-3 block">Wohlfühlen.</span>
            </h1>
          </div>
        </div>

        {/* Untertitel links, Buttons mittig unten */}
        <div className="relative w-full px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="cine cine-4 mb-4 text-sm sm:text-base text-gray-100 max-w-md drop-shadow text-left">{hero.hero_subheadline}</p>
            <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/speisekarte" data-testid="hero-menu-cta" className="inline-flex items-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] text-white font-semibold px-6 py-3.5 rounded-xl shadow-lg transition-all active:scale-95">
              Speisekarte ansehen <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/standorte" data-testid="hero-location-cta" className="inline-flex items-center gap-2 bg-white/95 hover:bg-white text-gray-900 font-semibold px-6 py-3.5 rounded-xl shadow-lg transition-all active:scale-95">
              <MapPin className="w-4 h-4" /> Standort finden
            </Link>
            <Link to="/helium-vorteile" className="inline-flex items-center gap-2 text-white font-semibold px-6 py-3.5 rounded-xl border border-white/60 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all">
              Helium Club entdecken
            </Link>
          </div>
          </div>
        </div>
      </section>

      {/* Personalized block */}
      {overview && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
          <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] text-white rounded-2xl p-6 sm:p-8 shadow-xl grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="home-personalized">
            <div>
              <div className="text-gray-400 text-sm">Willkommen zurück,</div>
              <div className="text-2xl font-bold font-display">{overview.user.first_name}</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Deine Punkte</div>
              <div className="text-2xl font-bold text-[#E0243A]" data-testid="home-points">{overview.user.points?.toLocaleString("de-DE")} Punkte</div>
            </div>
            <div>
              <div className="text-gray-400 text-sm">Nächste Prämie</div>
              <div className="text-lg font-semibold">{nextReward ? `${pointsToNext} Punkte bis „${nextReward.name}"` : "Alle Prämien erreicht 🎉"}</div>
            </div>
            <div className="flex items-center">
              <Link to="/mein-konto" className="inline-flex items-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] px-5 py-2.5 rounded-xl font-semibold text-sm">
                <QrCode className="w-4 h-4" /> Mein QR-Code
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Heute bei HELIUM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="mb-8">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#E0243A]">Heute bei HELIUM</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">{todayLabel()} – frisch für dich</h2>
        </div>
        {todayItems.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {todayItems.map((it, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden group">
                <div className="h-44 overflow-hidden"><img src={it.image} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{it.type}</span>
                    <span className="font-bold text-[#E0243A] text-right">{it.price?.toFixed(2)} €{it.ma_price != null && <span className="block text-xs font-semibold text-gray-500">MA {it.ma_price.toFixed(2)} €</span>}</span>
                  </div>
                  <h3 className="mt-1 text-lg font-semibold">{it.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{it.description}</p>
                  <button onClick={() => { navigator.share ? navigator.share({ title: it.name, text: `Heute bei HELIUM: ${it.name}` }).catch(()=>{}) : (navigator.clipboard.writeText(`Heute bei HELIUM: ${it.name}`), toast.success("Link kopiert")); }}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#E0243A]" data-testid="share-menu-item">
                    <Share2 className="w-4 h-4" /> Teilen
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-gray-500">Für heute ist kein Menü hinterlegt. <Link to="/wochenmenue" className="text-[#E0243A]">Ganzes Wochenmenü ansehen</Link></p>}
        <div className="mt-8"><Link to="/wochenmenue" className="inline-flex items-center gap-2 font-semibold text-[#E0243A]">Menü ansehen <ArrowRight className="w-4 h-4" /></Link></div>
      </section>

      {/* Offers */}
      {offers.length > 0 && (
        <section className="bg-[#F9F8F6] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#E0243A]">Aktuelle Angebote</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Spare bei deinen Favoriten</h2>
              </div>
              <Link to="/angebote" className="hidden sm:inline-flex items-center gap-2 font-semibold text-[#E0243A]">Alle Angebote <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="relative h-44"><img src={o.image} alt={o.title} className="w-full h-full object-cover" />
                    {o.discount && <span className="absolute top-3 left-3 bg-[#E0243A] text-white text-xs font-bold px-2.5 py-1 rounded-full">-{o.discount}</span>}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold">{o.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{o.description}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xl font-bold text-[#E0243A]">{o.price?.toFixed(2)} €</span>
                      {o.old_price && <span className="text-sm text-gray-400 line-through">{o.old_price?.toFixed(2)} €</span>}
                      {o.ma_price != null && o.ma_price !== "" && <span className="text-xs font-semibold text-gray-500">· MA {Number(o.ma_price).toFixed(2)} €</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Journey / Vorteile CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-3xl p-8 sm:p-12 text-white">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#E0243A]">Helium Club</span>
              <span data-testid="club-coming-soon" className="inline-flex items-center gap-2 bg-[#E0243A]/15 text-[#E0243A] text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-[#E0243A]/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E0243A] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E0243A]" />
                </span>
                Demnächst
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mt-2 font-display">Sammle Punkte. Sichere dir Prämien.</h2>
            <p className="text-gray-300 mt-3 max-w-lg">Unser Treueprogramm startet in Kürze! Bald sammelst du mit deinem persönlichen QR-Code bei jedem Besuch HELIUM Punkte – für gratis Kaffee, Snacks und mehr. Sei von Anfang an dabei und sichere dir deinen Vorsprung.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/registrieren" data-testid="home-register-cta" className="inline-flex items-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] px-6 py-3 rounded-xl font-semibold">Jetzt vormerken & Vorsprung sichern <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/helium-vorteile" className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 px-6 py-3 rounded-xl font-semibold">Mehr erfahren</Link>
            </div>
            <p className="mt-4 text-sm text-gray-400 flex items-center gap-2"><Star className="w-4 h-4 text-[#E0243A]" /> Frühe Mitglieder erhalten <span className="text-white font-semibold">120 Willkommenspunkte</span> zum Start.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
